import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Suspense,
  lazy,
} from 'react';
import useIsMobile from '../hooks/useIsMobile';
// import { X } from 'lucide-react'; // Remove X if only used in modal

// Lazy load the modal components
const LazyImageViewerModal = lazy(() => import('./ImageViewerModal'));
const LazyMobileImageViewerModal = lazy(
  () => import('./MobileImageViewerModal')
);

// Move style objects outside component for stable references
const initialSolidBackgroundStyle: React.CSSProperties = {
  backgroundColor: 'rgba(24, 18, 29, 0.7)',
  opacity: 1,
  transition: 'opacity 0.7s ease-in-out',
};

const videoGifBackgroundStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0,0,0,0.7)',
  opacity: 1,
  transition: 'opacity 0.7s ease-in-out',
};

interface ImageDisplayProps {
  imageUrl: string;
  alt?: string;
  round?: number;
  totalRounds?: number;
  // maxDimension no longer used for client-side optimization
  captionBeforeText?: string;
  captionAfterText?: string;
  hasGuessed?: boolean;
  isVideoProp?: boolean;
}

const ImageDisplay: React.FC<ImageDisplayProps> = ({
  imageUrl,
  alt = 'Guess the year',
  round,
  totalRounds = 10,
  captionBeforeText = '',
  captionAfterText = '',
  hasGuessed = false,
  isVideoProp,
}) => {
  const isVideo =
    isVideoProp !== undefined
      ? isVideoProp
      : imageUrl && /\.(mp4|webm)$/i.test(imageUrl);
  const isGif = imageUrl && /\.gif$/i.test(imageUrl);

  const [showFullSize, setShowFullSize] = useState(false);
  const isMobile = useIsMobile();

  const [backgroundStyles, setBackgroundStyles] = useState<React.CSSProperties>(
    initialSolidBackgroundStyle
  );
  const [isMainImageLoaded, setIsMainImageLoaded] = useState(false);

  const foregroundImageRef = useRef<HTMLImageElement | HTMLVideoElement | null>(
    null
  );

  // Reset loading state when imageUrl changes
  useEffect(() => {
    setIsMainImageLoaded(false);
    // No need to setBackgroundStyles here, the other effect will handle initial state
  }, [imageUrl]);

  // Apply blurred background effect OR solid color based on state
  useEffect(() => {
    if (isMainImageLoaded && imageUrl && !isVideo && !isGif) {
      // Show blurred background immediately without any transition
      setBackgroundStyles({
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(30px)',
        transform: 'scale(1.25)',
        opacity: 0.6, // Full opacity immediately
        transition: 'none',
      });
    } else if ((isVideo || isGif) && isMainImageLoaded) {
      setBackgroundStyles(videoGifBackgroundStyle);
    } else {
      setBackgroundStyles(initialSolidBackgroundStyle);
    }
  }, [isMainImageLoaded, imageUrl, isVideo, isGif]);

  // ⭐️ FIX: Check if media is already loaded from cache to prevent loading state deadlock ⭐️
  useEffect(() => {
    const mediaElement = foregroundImageRef.current;
    if (!mediaElement) return;

    const handleMediaLoaded = () => {
      if (!isMainImageLoaded) {
        setIsMainImageLoaded(true);
      }
    };

    if (mediaElement.tagName === 'IMG') {
      const img = mediaElement as HTMLImageElement;
      // If the image is already complete (e.g., from cache), immediately set loaded state.
      if (img.complete) {
        handleMediaLoaded();
      }
    } else if (mediaElement.tagName === 'VIDEO') {
      const vid = mediaElement as HTMLVideoElement;
      // If the video has enough data to play (readyState >= 3), set loaded state.
      // HAVE_FUTURE_DATA (3) or HAVE_ENOUGH_DATA (4) are sufficient.
      if (vid.readyState >= 3) {
        handleMediaLoaded();
      }
    }
    // The existing onLoad/onCanPlay handlers will catch cases where the media isn't cached.
  }, [imageUrl, isMainImageLoaded]); // Rerun if the URL changes or state needs re-checking

  const handleOpenModal = useCallback(() => {
    // Prevent opening modal if page is already pinch-zoomed on mobile
    if (isMobile && window.visualViewport && window.visualViewport.scale > 1) {
      return;
    }
    setShowFullSize(true);
  }, [isMobile]);
  const handleCloseModal = useCallback(() => setShowFullSize(false), []);

  const displayUrl = imageUrl;
  const fullscreenCaption = hasGuessed ? captionAfterText : captionBeforeText;

  // Common classes for the main image/video display area
  const mediaContainerClasses =
    'relative z-10 block mx-auto w-full h-full opacity-60';

  return (
    <div className="relative h-full min-h-0">
      <div
        className={`border-y md:border border-[#322A3F] md:rounded overflow-hidden flex items-center justify-center h-full bg-[#18121d] group relative cursor-pointer`}
        onClick={handleOpenModal}
      >
        {/* Blurred background div */}
        <div className="absolute inset-0" style={backgroundStyles} />

        {/* Round indicator for mobile */}
        {isMobile && round !== undefined && totalRounds && (
          <div className="absolute top-2 z-20 left-2 bg-black/60 text-white px-2 py-1 rounded-md text-[12px] uppercase font-bold">
            {round}/{totalRounds}
          </div>
        )}

        {/* Skeleton: Render if not loaded AND it's a static image (not video/GIF) */}
        {!isMainImageLoaded && !isVideo && !isGif && imageUrl && (
          <div className="absolute inset-0 z-10 bg-gray-700/30 animate-pulse"></div>
        )}

        {/* Image/Video: Always render if displayUrl exists, no opacity transition for instant appearance */}
        {/* This allows onLoad/onCanPlay to fire and set isMainImageLoaded */}
        {displayUrl &&
          (isVideo ? (
            <video
              key={displayUrl}
              ref={foregroundImageRef as React.RefObject<HTMLVideoElement>}
              src={displayUrl}
              className={`${mediaContainerClasses}`}
              style={{
                opacity: isMainImageLoaded ? 1 : 0,
                objectFit: 'contain',
              }}
              autoPlay
              loop
              muted
              playsInline
              onCanPlay={() => {
                console.log('Video can play:', displayUrl);
                setIsMainImageLoaded(true);
              }}
              onLoadedData={() => {
                if (
                  foregroundImageRef.current &&
                  (foregroundImageRef.current as HTMLVideoElement).readyState >=
                    3
                ) {
                }
              }}
              onError={() => {
                console.error('Main video failed:', displayUrl);
                setIsMainImageLoaded(true);
              }} // Set loaded true to hide skeleton on error
            />
          ) : (
            // Covers static images and GIFs
            <img
              key={displayUrl}
              ref={foregroundImageRef as React.RefObject<HTMLImageElement>}
              src={displayUrl}
              alt={alt}
              className={`${mediaContainerClasses}`}
              style={{
                opacity: isMainImageLoaded ? 1 : 0,
                objectFit: 'contain',
              }}
              decoding="async"
              fetchPriority="high"
              onLoad={() => {
                console.log('Foreground Image Loaded:', displayUrl);
                setIsMainImageLoaded(true);
              }}
              onError={() => {
                console.error('Foreground Image FAILED to load:', displayUrl);
                setIsMainImageLoaded(true);
              }} // Set loaded true to hide skeleton on error
            />
          ))}

        {/* Fullscreen button etc. */}
        {!isMobile && (
          <button
            className="absolute bottom-2 right-2 bg-black/50 text-white p-1.5 rounded-full group-hover:bg-black/70 transition-all z-20"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
          </button>
        )}
      </div>

      {showFullSize && (
        <Suspense fallback={null}>
          {isMobile ? (
            <LazyMobileImageViewerModal
              imageUrl={imageUrl}
              caption={fullscreenCaption}
              onClose={handleCloseModal}
              alt={alt}
            />
          ) : (
            <LazyImageViewerModal
              imageUrl={imageUrl}
              caption={fullscreenCaption}
              onClose={handleCloseModal}
              alt={alt}
            />
          )}
        </Suspense>
      )}
    </div>
  );
};

export default ImageDisplay;
